/**
 * Device action node definition.
 */

import type { NodeDefinition } from '../types';
import type { FlowNode } from '../../../types/flow-types';
import { get } from 'svelte/store';
import { devices } from 'src/stores';
import {
	createDeviceAction,
	createGuiDeviceId,
	convertUiPropertyToSceneCommand,
	convertUiValueToSceneValue,
	delay_action,
} from '../../../converters/helpers';
import { createHexToDecimalOrId } from 'src/helpers/utilities';

export const deviceActionDefinition: NodeDefinition = {
	type: 'action',
	category: 'action',
	sourceHandles: ['output'],

	defaultData: () => ({
		label: 'Action',
		device: undefined,
		actions: [],
	}),

	toSceneJson: (node: FlowNode) => {
		if (node.data.sceneData) {
			const sceneData = { ...(node.data.sceneData as any) };
			sceneData._fid = sceneData._fid || node.id;
			return sceneData;
		}
		if (node.data.originalAction) {
			const original = { ...(node.data.originalAction as any) };
			original._fid = original._fid || node.id;
			return original;
		}

		if (!node.data.device || !node.data.actions?.[0]) return null;

		const deviceKey = createGuiDeviceId({
			id: String(node.data.device.id ?? ''),
			channel: Number(node.data.device.channel ?? 0) || 0,
		} as any);
		const device = get(devices)?.[deviceKey];
		const type: string = String(device?.type ?? node.data.device_type ?? 'relay');
		const mode: string = String(device?.mode ?? node.data.device_mode ?? 'relay');

		// Ensure device.id is string (is_device_action_ui requires it)
		const deviceId = String(node.data.device.id ?? '');
		const channel = Number(node.data.device.channel ?? 0) || 0;

		const element = {
			device: {
				id: deviceId,
				channel: (channel > 0 ? channel : '0') as number | '0',
			},
			actions: node.data.actions,
			delay: node.data.delay,
			timer: node.data.timer,
		} as any;

		let result = createDeviceAction(element, type, mode);

		// Fallback when createDeviceAction returns undefined (e.g. device not in store, strategy mismatch)
		if (!result && node.data.actions?.[0]) {
			const primary = node.data.actions[0] as { name?: string; value?: unknown };
			const actionName = String(primary.name || 'turn');
			const hexToDecimal = createHexToDecimalOrId(deviceId);
			const command: string = convertUiPropertyToSceneCommand(actionName, channel);
			let value: unknown = primary.value;
			// Normalize turn/state on/off -> 1/0 for scene format
			if (actionName === 'turn' || actionName === 'state') {
				value = (convertUiValueToSceneValue as (t: string, v: unknown) => unknown)(
					command,
					value,
				);
			}
			result = {
				_gui_purpose: 'action',
				r: `dev:${hexToDecimal}:${command}`,
				set: value,
			} as any;
			if (node.data.delay?.value > 0) {
				result = delay_action(result, node.data.delay) as any;
			}
		}

		return result;
	},
};
